/**
 * Sub-Division Analyser — DISCOM role screen (FR-D08 to FR-D10)
 * Bottom-up filters: Circle → Division. Table always at sub-division level.
 */
import React, { useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import {
  pgvclSubDivisions,
  pgvclAvgSpecificYield,
} from '../../data/mockData';

const prCls = (pr: number) =>
  pr >= 75 ? 'text-green-600 font-semibold' :
  pr >= 65 ? 'text-amber-600 font-semibold' :
             'text-red-600 font-semibold';

// ── Tile definitions ──────────────────────────────────────────────────────────
type SubDivRow = typeof pgvclSubDivisions[0];

interface TileDef {
  label: string;
  textCls: string;
  bgCls: string;
  ringCls: string;
  filterFn: (d: SubDivRow) => boolean;
}

const TILES: TileDef[] = [
  {
    label: 'PR below 70%',
    textCls: 'text-red-600', bgCls: 'bg-red-50', ringCls: 'ring-red-500',
    filterFn: d => d.prPercent < 70,
  },
  {
    label: 'Generation dropping MoM',
    textCls: 'text-amber-600', bgCls: 'bg-amber-50', ringCls: 'ring-amber-500',
    filterFn: d => d.momTrend < 0,
  },
  {
    label: 'Low availability',
    textCls: 'text-green-700', bgCls: 'bg-green-50', ringCls: 'ring-green-500',
    filterFn: d => d.availPercent < 80,
  },
  {
    label: 'Low specific yield',
    textCls: 'text-teal-600', bgCls: 'bg-teal-50', ringCls: 'ring-teal-500',
    filterFn: d => d.specificYield < pgvclAvgSpecificYield,
  },
  {
    label: 'High alarm rate',
    textCls: 'text-purple-600', bgCls: 'bg-purple-50', ringCls: 'ring-purple-500',
    filterFn: d => d.alarms / d.plants > 0.5,
  },
  {
    label: 'High offline rate',
    textCls: 'text-orange-600', bgCls: 'bg-orange-50', ringCls: 'ring-orange-500',
    filterFn: d => (1 - d.availPercent / 100) > 0.05,
  },
];

// Tile counts always reflect DISCOM totals (not filtered view)
const TILE_COUNTS = TILES.map(t => pgvclSubDivisions.filter(t.filterFn).length);

// Unique circles and divisions lookup
const ALL_CIRCLES   = Array.from(new Set(pgvclSubDivisions.map(d => d.circle))).sort();
const divisionsByCircle = ALL_CIRCLES.reduce<Record<string, string[]>>((acc, c) => {
  acc[c] = Array.from(new Set(pgvclSubDivisions.filter(d => d.circle === c).map(d => d.division))).sort();
  return acc;
}, {});

export default function DiscomSubDivisionAnalyser() {
  const [activeTile,      setActiveTile]      = useState<number | null>(null);
  const [selectedCircle,  setSelectedCircle]  = useState<string>('');
  const [selectedDivision,setSelectedDivision]= useState<string>('');

  const divisionsForCircle = selectedCircle ? (divisionsByCircle[selectedCircle] ?? []) : [];

  const visibleRows = useMemo(() => {
    let rows = pgvclSubDivisions;
    if (selectedCircle)   rows = rows.filter(d => d.circle   === selectedCircle);
    if (selectedDivision) rows = rows.filter(d => d.division === selectedDivision);
    if (activeTile !== null) rows = rows.filter(TILES[activeTile].filterFn);
    return rows;
  }, [selectedCircle, selectedDivision, activeTile]);

  const exportCSV = () => {
    const rows = [
      'Name,Circle,Division,Plants,kWp,kWh Today,Sp.Yield,PR%,Avail%,CUF%,MoM%,Alarms',
      ...visibleRows.map(d =>
        `${d.name},${d.circle},${d.division},${d.plants},${d.capacityKWp},${d.generationKWh},${d.specificYield},${d.prPercent},${d.availPercent},${d.cufPercent},${d.momTrend},${d.alarms}`)
    ];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = Object.assign(document.createElement('a'), { href: url, download: 'sub-division-analyser.csv' });
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 space-y-5">

      {/* ── Triage tiles (6) ── */}
      <div className="grid grid-cols-6 gap-3">
        {TILES.map((t, i) => {
          const isActive = activeTile === i;
          return (
            <div
              key={t.label}
              onClick={() => setActiveTile(prev => prev === i ? null : i)}
              className={`rounded-card border p-4 cursor-pointer transition-all hover:shadow-md ${t.bgCls}
                ${isActive ? `ring-2 ${t.ringCls} border-transparent` : 'border-border shadow-card'}`}
            >
              <p className="text-xs text-text-secondary font-semibold leading-tight">{t.label}</p>
              <p className={`text-2xl font-bold mt-1 ${t.textCls}`}>{TILE_COUNTS[i]}</p>
              <p className="text-xs text-text-tertiary mt-1">sub-divisions</p>
            </div>
          );
        })}
      </div>

      {/* ── Filter bar ── */}
      <div className="flex items-center gap-3">
        <select
          value={selectedCircle}
          onChange={e => { setSelectedCircle(e.target.value); setSelectedDivision(''); }}
          className="text-xs border border-border rounded-input px-2.5 py-1.5 bg-white focus:outline-none focus:border-accent"
        >
          <option value="">All Circles</option>
          {ALL_CIRCLES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        <select
          value={selectedDivision}
          onChange={e => setSelectedDivision(e.target.value)}
          disabled={!selectedCircle}
          className="text-xs border border-border rounded-input px-2.5 py-1.5 bg-white focus:outline-none focus:border-accent disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <option value="">All Divisions</option>
          {divisionsForCircle.map(d => <option key={d} value={d}>{d}</option>)}
        </select>

        {activeTile !== null && (
          <button
            onClick={() => setActiveTile(null)}
            className="text-xs text-accent hover:underline px-2 py-1 border border-accent/30 rounded-input"
          >
            Clear filter
          </button>
        )}

        <div className="flex-1" />
        <button
          onClick={exportCSV}
          className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-input border border-border bg-white text-text-secondary hover:text-accent hover:border-accent transition-colors"
        >
          <Download size={12} /> Export CSV
        </button>
      </div>

      {/* ── Table ── */}
      <div className="bg-white rounded-card border border-border shadow-card overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <p className="text-xs font-semibold text-text-primary">
            {visibleRows.length} sub-division{visibleRows.length !== 1 ? 's' : ''}
            {selectedCircle   ? ` · ${selectedCircle}`   : ''}
            {selectedDivision ? ` · ${selectedDivision} Division` : ''}
            {activeTile !== null ? ` · ${TILES[activeTile].label}` : ''}
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bg-secondary border-b border-border">
              <tr>
                <th className="text-left py-2.5 px-4 text-text-secondary font-semibold">Name</th>
                <th className="text-left py-2.5 px-3 text-text-secondary font-semibold">Circle</th>
                <th className="text-left py-2.5 px-3 text-text-secondary font-semibold">Division</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Plants</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Capacity</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Gen today</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Sp. yield</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">PR %</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Avail %</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">CUF %</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">MoM %</th>
                <th className="text-left py-2.5 px-3 text-text-secondary font-semibold">Active / Offline</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Alarms</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map(d => {
                const activePl  = Math.round(d.plants * d.availPercent / 100);
                const offlinePl = d.plants - activePl;
                return (
                  <tr key={d.id} className="border-b border-border/50 hover:bg-bg-secondary transition-colors">
                    <td className="py-2.5 px-4 font-medium text-text-primary">{d.name}</td>
                    <td className="py-2.5 px-3 text-text-tertiary">{d.circle}</td>
                    <td className="py-2.5 px-3 text-text-tertiary">{d.division}</td>
                    <td className="py-2.5 px-3 text-right text-text-secondary">{d.plants.toLocaleString()}</td>
                    <td className="py-2.5 px-3 text-right text-text-secondary">{d.capacityKWp.toLocaleString()} kWp</td>
                    <td className="py-2.5 px-3 text-right font-medium text-text-primary">{d.generationKWh.toLocaleString()} kWh</td>
                    <td className="py-2.5 px-3 text-right text-text-secondary">{d.specificYield.toFixed(2)} kWh/kWp</td>
                    <td className={`py-2.5 px-3 text-right ${prCls(d.prPercent)}`}>{d.prPercent.toFixed(1)}%</td>
                    <td className={`py-2.5 px-3 text-right ${d.availPercent < 80 ? 'text-amber-600 font-semibold' : 'text-text-secondary'}`}>{d.availPercent.toFixed(0)}%</td>
                    <td className="py-2.5 px-3 text-right text-text-secondary">{d.cufPercent.toFixed(1)}%</td>
                    <td className={`py-2.5 px-3 text-right font-semibold ${d.momTrend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {d.momTrend >= 0 ? '↑' : '↓'}{Math.abs(d.momTrend).toFixed(1)}%
                    </td>
                    <td className="py-2.5 px-3 text-xs text-text-secondary">
                      {activePl} active · <span className={offlinePl > d.plants * 0.05 ? 'text-red-600 font-semibold' : ''}>{offlinePl} offline</span>
                    </td>
                    <td className={`py-2.5 px-3 text-right ${d.alarms > 0 ? 'text-red-600 font-semibold' : 'text-text-secondary'}`}>{d.alarms}</td>
                  </tr>
                );
              })}
              {visibleRows.length === 0 && (
                <tr>
                  <td colSpan={13} className="py-10 text-center text-xs text-text-tertiary">
                    No sub-divisions match the current filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
